export type HorizonId = 1 | 3 | 6 | 12;

export interface Horizon {
  id: HorizonId;
  label: string;
}

export const HORIZONS: Horizon[] = [
  { id: 1, label: 'Месяц' },
  { id: 3, label: 'Квартал' },
  { id: 6, label: 'Полгода' },
  { id: 12, label: 'Год' },
];

interface Window {
  from: Date;
  to: Date;
  /** Сколько месяцев входит в окно (для plan × N). */
  planMultiplier: number;
  /** Подпись периода для UI. */
  label: string;
  /** Сколько дней прошло (для подсказки «период идёт»). */
  daysElapsed: number;
  /** Общая длина окна в днях. */
  daysTotal: number;
}

const RU_MONTHS = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];

export interface GetWindowOpts {
  now?: Date;
  /** ISO-дата начала статистики: окно обрезается слева, план пересчитывается по покрытым месяцам. */
  statsSince?: string | null;
}

/**
 * Возвращает дату-окно для горизонта.
 * Все горизонты привязаны к календарю и включают будущие операции внутри периода.
 */
export function getWindow(h: HorizonId, opts: GetWindowOpts = {}): Window {
  const now = opts.now ?? new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  if (h === 1) {
    const from = new Date(y, m, 1, 0, 0, 0, 0);
    const to = new Date(y, m + 1, 0, 23, 59, 59, 999);
    return finalize(from, to, 1, `${capitalize(monthName(m))} ${y}`, now, opts.statsSince);
  }

  if (h === 3) {
    // квартал текущего месяца
    const qStartMonth = Math.floor(m / 3) * 3;
    const from = new Date(y, qStartMonth, 1, 0, 0, 0, 0);
    const to = new Date(y, qStartMonth + 3, 0, 23, 59, 59, 999);
    const qNum = Math.floor(m / 3) + 1;
    const label = `Q${qNum} ${y} · ${RU_MONTHS[qStartMonth]}–${RU_MONTHS[qStartMonth + 2]}`;
    return finalize(from, to, 3, label, now, opts.statsSince);
  }

  if (h === 6) {
    // полугодие
    const isFirstHalf = m < 6;
    const halfStart = isFirstHalf ? 0 : 6;
    const from = new Date(y, halfStart, 1, 0, 0, 0, 0);
    const to = new Date(y, halfStart + 6, 0, 23, 59, 59, 999);
    const label = `${isFirstHalf ? 'H1' : 'H2'} ${y} · ${RU_MONTHS[halfStart]}–${RU_MONTHS[halfStart + 5]}`;
    return finalize(from, to, 6, label, now, opts.statsSince);
  }

  // год
  const from = new Date(y, 0, 1, 0, 0, 0, 0);
  const to = new Date(y, 12, 0, 23, 59, 59, 999);
  return finalize(from, to, 12, `${y} год`, now, opts.statsSince);
}

function finalize(
  from: Date,
  to: Date,
  mult: number,
  label: string,
  now: Date,
  statsSince?: string | null,
): Window {
  // stats_since обрезает окно слева: операции до даты не считаются,
  // а план масштабируется на реально покрытые месяцы.
  const since = statsSince ? parseIsoDate(statsSince) : null;
  if (since && since > from && since <= to) {
    from = since;
    mult =
      (to.getFullYear() * 12 + to.getMonth()) -
      (from.getFullYear() * 12 + from.getMonth()) + 1;
    label += ` · с ${since.getDate()} ${RU_MONTHS[since.getMonth()]}`;
  }
  const daysTotal = Math.round((to.getTime() - from.getTime()) / (24 * 3600 * 1000));
  const elapsedRaw = Math.round((now.getTime() - from.getTime()) / (24 * 3600 * 1000));
  const daysElapsed = Math.max(0, Math.min(daysTotal, elapsedRaw));
  return { from, to, planMultiplier: mult, label, daysElapsed, daysTotal };
}

function monthName(zeroBasedIdx: number): string {
  const full = ['январь','февраль','март','апрель','май','июнь','июль','август','сентябрь','октябрь','ноябрь','декабрь'];
  return full[((zeroBasedIdx % 12) + 12) % 12];
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Дней в текущем календарном месяце (28/29/30/31). */
export function daysInCurrentMonth(now: Date = new Date()): number {
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
}

export function parseIsoDate(s: string): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isInWindow(opDateIso: string, w: Window): boolean {
  const d = parseIsoDate(opDateIso);
  if (!d) return false;
  return d >= w.from && d <= w.to;
}
